import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download, Layers } from 'lucide-react';

/** Replaces the results summary while one or more results are selected. */
export default function SelectionBar({
  selectedCount,
  allSelected,
  selectAllLabel,
  canAddToGroup,
  onToggleVisible,
  onAddToGroup,
  onClear,
}: {
  selectedCount: number;
  allSelected: boolean;
  selectAllLabel: string;
  canAddToGroup: boolean;
  onToggleVisible: () => void;
  onAddToGroup: () => void;
  onClear: () => void;
}) {
  const selectAllRef = useRef<HTMLInputElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
    }
  }, [selectedCount, allSelected]);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handler = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionsMenuOpen]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm">
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allSelected}
          onChange={onToggleVisible}
          aria-label={selectAllLabel}
          className="h-4 w-4 accent-emerald-700 cursor-pointer"
        />
        <span className="font-semibold text-gray-700">
          {selectedCount} selected
        </span>
      </label>

      <div className="relative" ref={actionsMenuRef}>
        <button
          type="button"
          onClick={() => setActionsMenuOpen((open) => !open)}
          className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          aria-haspopup="true"
          aria-expanded={actionsMenuOpen}
        >
          Actions
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${actionsMenuOpen ? 'rotate-180' : ''}`} />
        </button>
        {actionsMenuOpen && (
          <div className="absolute left-0 top-full z-50 pt-1 min-w-[220px]">
            <div role="menu" className="rounded-xl border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5">
              {canAddToGroup && (
                <button
                  type="button"
                  role="menuitem"
                  className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
                  onClick={() => {
                    setActionsMenuOpen(false);
                    onAddToGroup();
                  }}
                >
                  <Layers className="w-4 h-4" />
                  Add to group
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                disabled
                className="w-full px-3 py-2.5 text-left text-sm font-medium text-gray-300 flex items-center gap-2 cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Download - coming soon...
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onClear}
        className="ml-auto text-sm font-medium text-gray-500 hover:text-gray-800"
      >
        Clear
      </button>
    </div>
  );
}
